import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useSpecificationCategories } from '@/hooks/useSpecificationCategories';
import { useSpecificationFieldDefinitions } from '@/hooks/useSpecificationFieldDefinitions';
import { SpecificationFieldInput } from './SpecificationFieldInput';
import { useToast } from '@/hooks/use-toast';
import { Loader2, FileText, Upload, MessageSquare } from 'lucide-react';
import { AISpecificationChat } from './AISpecificationChat';
import { UploadSpecificationForm } from './UploadSpecificationForm';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { mapProductCategoryToJobCategory } from '@/lib/utils/categoryMapping';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

interface MasterSpecificationBuilderProps {
  existingSpecification?: any;
  isEditMode?: boolean;
  onSave: (specData: any) => void;
  onCancel: () => void;
}

export function MasterSpecificationBuilder({ 
  existingSpecification, 
  isEditMode = false,
  onSave, 
  onCancel 
}: MasterSpecificationBuilderProps) {
  const { toast } = useToast();
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState(
    existingSpecification?.content?.product_category_id || ''
  );
  const [specificationData, setSpecificationData] = useState<Record<string, Record<string, any>>>(
    existingSpecification?.content?.specification_data || {}
  );
  const [additionalNotes, setAdditionalNotes] = useState(
    existingSpecification?.content?.additional_notes || ''
  );
  const [specificationName, setSpecificationName] = useState(
    existingSpecification?.specification_name || ''
  );
  const [saving, setSaving] = useState(false);
  const [inputMethod, setInputMethod] = useState<'form' | 'upload' | 'ai' | null>(null);
  
  const { categories: productCategories, loading: loadingProductCategories } = useProductCategories();
  const { categories: specCategories, loading: loadingSpecCategories } = useSpecificationCategories();
  const { fields: fieldDefinitions, loading: loadingFields } = useSpecificationFieldDefinitions();
  
  // Helper function to convert slug-based keys to UUID-based keys
  const mapSlugKeysToUUIDs = (slugBasedData: any): any => {
    if (!slugBasedData || typeof slugBasedData !== 'object') return slugBasedData;
    
    const mappedData: any = {};
    
    // Helper to convert name to slug format (e.g., "Bindery & Folding" -> "bindery_folding")
    const toSlug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    
    // Create lookup maps for categories and fields using slugified names
    const categorySlugToUUID = new Map(
      specCategories.map(cat => [toSlug(cat.name), cat.id])
    );
    
    const fieldSlugToUUID = new Map(
      fieldDefinitions.map(field => [toSlug(field.field_name), field.id])
    );
    
    // Process each category in the slug-based data
    Object.entries(slugBasedData).forEach(([categoryKey, categoryValue]) => {
      // Check if the key is already a UUID
      const isUUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(categoryKey);
      
      if (isUUID) {
        // Already a UUID, keep as is
        mappedData[categoryKey] = categoryValue;
      } else {
        // It's a slug, map to UUID
        const categoryUUID = categorySlugToUUID.get(categoryKey);
        if (categoryUUID && typeof categoryValue === 'object' && categoryValue !== null) {
          const mappedFields: any = {};
          
          // Map field slugs to UUIDs within this category
          Object.entries(categoryValue).forEach(([fieldKey, fieldValue]) => {
            const isFieldUUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(fieldKey);
            
            if (isFieldUUID) {
              mappedFields[fieldKey] = fieldValue;
            } else {
              const fieldUUID = fieldSlugToUUID.get(fieldKey);
              if (fieldUUID) {
                mappedFields[fieldUUID] = fieldValue;
              }
            }
          });
          
          mappedData[categoryUUID] = mappedFields;
        }
      }
    });
    
    return mappedData;
  };
  
  // Pre-populate data when editing an existing specification
  useEffect(() => {
    if (isEditMode && existingSpecification?.content) {
      const content = existingSpecification.content;
      
      // Pre-populate product category
      if (content.product_category_id) {
        setSelectedProductCategoryId(content.product_category_id);
      }
      
      // Pre-populate specification name
      if (existingSpecification.specification_name) {
        setSpecificationName(existingSpecification.specification_name);
      }
      
      // Pre-populate specification data (field values)
      if (content.specification_data) {
        // Map slug-based keys to UUIDs if needed (for AI-generated specs)
        const mappedData = mapSlugKeysToUUIDs(content.specification_data);
        setSpecificationData(mappedData);
      }
      
      // Pre-populate additional notes
      if (content.additional_notes) {
        setAdditionalNotes(content.additional_notes);
      }

      // Set input method based on whether it's upload or form-based
      if (existingSpecification.specification_document_id) {
        setInputMethod('upload');
      } else {
        setInputMethod('form');
      }
    }
  }, [isEditMode, existingSpecification, specCategories, fieldDefinitions]);
  
  // Filter spec categories by selected product category
  const relevantSpecCategories = specCategories.filter(
    sc => sc.product_category_id === selectedProductCategoryId && sc.is_active
  );
  
  // Track spec started when category is selected
  useEffect(() => {
    if (selectedProductCategoryId && !isEditMode) {
      const category = productCategories.find(pc => pc.id === selectedProductCategoryId);
      analytics.track({
        name: ANALYTICS_EVENTS.SPEC_STARTED,
        category: 'engagement',
        properties: {
          spec_category: category?.name,
        },
      });
    }
  }, [selectedProductCategoryId, isEditMode, productCategories]);
  
  // For each spec category, get its fields
  const categoryFieldsMap = relevantSpecCategories.reduce((acc, category) => {
    acc[category.id] = fieldDefinitions.filter(
      field => field.category_id === category.id && field.is_active
    );
    return acc;
  }, {} as Record<string, any[]>);
  
  const handleFieldChange = (categoryId: string, fieldId: string, value: any) => {
    setSpecificationData(prev => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || {}),
        [fieldId]: value
      }
    }));
  };
  
  const validateRequiredFields = () => {
    const errors: string[] = [];
    
    relevantSpecCategories.forEach(category => {
      const fields = categoryFieldsMap[category.id] || [];
      fields.forEach(field => {
        if (field.is_required) {
          const value = specificationData[category.id]?.[field.id];
          if (!value || (Array.isArray(value) && value.length === 0)) {
            errors.push(`${category.name}: ${field.field_label} is required`);
          }
        }
      });
    });
    
    return errors;
  };
  
  const handleSave = async () => {
    if (!specificationName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Specification name is required',
        variant: 'destructive',
      });
      return;
    }
    
    const errors = validateRequiredFields();
    
    if (errors.length > 0) {
      toast({
        title: 'Missing required fields',
        description: errors.join(', '),
        variant: 'destructive',
      });
      return;
    }
    
    setSaving(true);
    try {
      const selectedCategory = productCategories.find(pc => pc.id === selectedProductCategoryId);
      
      // Build structured specification data
      const templateData = {
        specification_name: specificationName.trim(),
        specification_description: `Master specification template for ${selectedCategory?.name}`,
        category: mapProductCategoryToJobCategory(selectedCategory?.name || ''),
        specification_type: 'personal',
        content: {
          product_category_id: selectedProductCategoryId,
          specification_data: specificationData,
          additional_notes: additionalNotes,
        },
        sections: additionalNotes ? [{
          title: 'Additional Notes',
          content: additionalNotes,
          required: false
        }] : [],
        required_fields: [],
        tags: ['master-template', selectedCategory?.name?.toLowerCase() || ''],
      };
      
      // Track spec completed
      analytics.track({
        name: ANALYTICS_EVENTS.SPEC_COMPLETED,
        category: 'engagement',
        properties: {
          spec_category: selectedCategory?.name,
          input_method: inputMethod || 'form',
          field_count: Object.values(specificationData).reduce((count, cat) => count + Object.keys(cat || {}).length, 0),
        },
      });

      await onSave(templateData);
    } catch (error) {
      console.error('Error saving specification:', error);
      toast({
        title: 'Error',
        description: 'Failed to save specification',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const selectedCategory = productCategories.find(pc => pc.id === selectedProductCategoryId);
  
  if (loadingProductCategories) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {!selectedProductCategoryId ? (
        <div className="space-y-4">
          <div>
            <Label className="text-base">Select Product Category</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Choose a product category to load the master specification template
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {productCategories.filter(pc => pc.is_active).map(pc => (
              <Card 
                key={pc.id}
                onClick={() => setSelectedProductCategoryId(pc.id)}
                className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
              >
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg">{pc.name}</h3>
                  {pc.description && (
                    <p className="text-sm text-muted-foreground mt-1">{pc.description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : selectedProductCategoryId && !inputMethod ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Choose Input Method</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Select how you'd like to provide specification details for {selectedCategory?.name}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedProductCategoryId('')}
            >
              ← Back to Categories
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card 
              onClick={() => setInputMethod('ai')}
              className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-purple-500/10 flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-purple-500" />
                  </div>
                  <h3 className="font-semibold">Chat with AI</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Conversational AI assistant to gather specifications naturally
                </p>
              </CardContent>
            </Card>

            <Card 
              onClick={() => setInputMethod('form')}
              className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Structured Form</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  For experienced users and more advanced specifications
                </p>
              </CardContent>
            </Card>
            
            <Card 
              onClick={() => setInputMethod('upload')}
              className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-blue-500/10 flex-shrink-0">
                    <Upload className="h-5 w-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold">Upload Document</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Upload your own specification file (PDF, DOCX, XLSX, etc.)
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : inputMethod === 'upload' ? (
        <UploadSpecificationForm
          productCategoryId={selectedProductCategoryId}
          productCategoryName={selectedCategory?.name || ''}
          onSave={onSave}
          onBack={() => setInputMethod(null)}
          onCancel={onCancel}
          existingSpecification={existingSpecification}
          isEditMode={isEditMode}
        />
      ) : inputMethod === 'ai' ? (
        <AISpecificationChat
          productCategoryId={selectedProductCategoryId}
          productCategoryName={selectedCategory?.name || ''}
          onSave={onSave}
          onBack={() => setInputMethod(null)}
          onCancel={onCancel}
        />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                Master Template: {selectedCategory?.name}
              </h3>
              <p className="text-sm text-muted-foreground">
                Fill in the relevant fields below. Fields marked with * are required.
              </p>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedProductCategoryId('');
                        setSpecificationData({});
                        setAdditionalNotes('');
                        setSpecificationName('');
                      }}
                      disabled={isEditMode}
                    >
                      Change Category
                    </Button>
                  </span>
                </TooltipTrigger>
                {isEditMode && (
                  <TooltipContent>
                    <p>Cannot change category when editing an existing specification</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="space-y-2">
            <Label htmlFor="spec-name">
              Specification Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="spec-name"
              value={specificationName}
              onChange={(e) => setSpecificationName(e.target.value)}
              placeholder={`e.g., ${selectedCategory?.name} - Standard Specification`}
              className="w-full"
            />
          </div>
          
          {loadingSpecCategories || loadingFields ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : relevantSpecCategories.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No specification categories configured for this product category yet.
                <br />
                Contact an administrator to set up the master template.
              </CardContent>
            </Card>
          ) : (
            <Accordion type="multiple" className="space-y-4" defaultValue={relevantSpecCategories.map(c => c.id)}>
              {relevantSpecCategories.map(specCategory => {
                const fields = categoryFieldsMap[specCategory.id] || [];
                
                if (fields.length === 0) return null;
                
                return (
                  <AccordionItem key={specCategory.id} value={specCategory.id}>
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      {specCategory.name}
                      {specCategory.description && (
                        <span className="text-sm font-normal text-muted-foreground ml-2">
                          — {specCategory.description}
                        </span>
                      )}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                        {fields.map(field => (
                          <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                            <SpecificationFieldInput
                              field={field}
                              value={specificationData[specCategory.id]?.[field.id] || ''}
                              onChange={(value) => handleFieldChange(specCategory.id, field.id, value)}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Add any custom notes or requirements not covered by the standard fields..."
                rows={4}
              />
            </CardContent>
          </Card>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditMode ? 'Update Specification' : 'Save Specification'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
